import type { QuoteDraftInput } from '@repo/api-contracts';
import { useState } from 'react';
import { useCachedQuoteDetail, useInvalidateQuotes } from '@/entities/quote';
import { trpc } from '@/shared/api';
import {
  getErrorMessage,
  getServerFieldErrors,
  getTrpcErrorCode,
  type ServerFieldErrors,
} from '@/shared/lib';
import { draftMatchesInput } from './draft-form';

/**
 * 저장 결과 — 성공하면 다음 저장·발행에 쓸 초안 토큰을, 실패하면 서버가 실은 칸 오류(점 키 `lines.{i}.{칸}`, 없으면 빈 묶음)를
 * 함께 돌려준다. 칸 오류를 화면 줄에 붙이는 일은 보낸 폼을 아는 편집기가 한다.
 */
export type SaveQuoteDraftOutcome =
  | { readonly ok: true; readonly draftUpdatedAt: string }
  | { readonly ok: false; readonly fieldErrors: ServerFieldErrors };

export interface UseSaveQuoteDraftResult {
  /** 서버 거절·버전 충돌이면 `{ ok: false }`. */
  readonly save: (input: QuoteDraftInput) => Promise<SaveQuoteDraftOutcome>;
  readonly isPending: boolean;
  readonly formError: string | null;
  /**
   * 보낸 초안이 현재 초안이 아니거나(다른 운영자가 먼저 저장·발행 — QUOTE_VERSION_OUTDATED) 상태가 바뀐(QUOTE_STATE_CONFLICT)
   * 상태 — 편집 중인 입력은 호출부가 그대로 두고, "최신 초안 다시 불러오기"는 운영자가 고른다.
   */
  readonly isOutdated: boolean;
  readonly reset: () => void;
}

const FORM_ERROR_FALLBACK = '초안을 저장하지 못했습니다.';

/**
 * 초안 임시 저장(quoteManagement.saveDraft, 견적 설계 §6-3). 줄 정렬·금액·합계는 서버가 계산하므로
 * 성공하면 상세를 다시 불러와 저장값을 받는다. CONFLICT 는 폼 오류가 아니라 "최신 초안 안내"다 —
 * 자동으로 다시 불러오면 편집 중인 줄이 사라지므로 호출부의 명시적 선택에 맡긴다.
 *
 * 낙관적 동시성: 다시 불러온 초안이 방금 저장한 내용과 같으면 그 초안의 `updatedAt` 을 새 토큰으로 돌려준다. 다르면(재조회 전에
 * 다른 운영자가 저장) 보낸 토큰을 그대로 돌려줘 다음 저장·발행이 서버에서 충돌로 막히게 한다 — 남의 내용을 내 화면으로 덮지 않는다.
 */
export const useSaveQuoteDraft = (): UseSaveQuoteDraftResult => {
  const [formError, setFormError] = useState<string | null>(null);
  const [isOutdated, setIsOutdated] = useState(false);
  const invalidate = useInvalidateQuotes();
  const cachedDetail = useCachedQuoteDetail();
  const mutation = trpc.quoteManagement.saveDraft.useMutation();

  const save = async (input: QuoteDraftInput): Promise<SaveQuoteDraftOutcome> => {
    setFormError(null);
    setIsOutdated(false);

    try {
      await mutation.mutateAsync(input);
      await invalidate.detail(input.quoteRequestId);
      const draft = cachedDetail(input.quoteRequestId)?.draft ?? null;
      const draftUpdatedAt =
        draft !== null && draftMatchesInput(draft, input)
          ? draft.updatedAt
          : input.expectedDraftUpdatedAt;
      return { ok: true, draftUpdatedAt };
    } catch (error) {
      if (getTrpcErrorCode(error) === 'CONFLICT') {
        setIsOutdated(true);
      } else {
        setFormError(getErrorMessage(error, FORM_ERROR_FALLBACK));
      }

      return { ok: false, fieldErrors: getServerFieldErrors(error) ?? {} };
    }
  };

  return {
    save,
    isPending: mutation.isPending,
    formError,
    isOutdated,
    reset: () => {
      setFormError(null);
      setIsOutdated(false);
    },
  };
};
